import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Auth } from "shared/@auth/Auth";
import Button, { ButtonPurpose } from "shared/Button";
import { Column, Row } from "shared/layout";
import Loader from "shared/Loader";
import LoadingContainer from "shared/LoadingContainer";
import PagingControls from "shared/PagingControls";
import Table from "shared/Table";
import formatDate from "shared/@utils/formatDate";
import Details from "./Details";
import styles from "./index.module.css";
import Items from "./Items";
import RefundForm from "./RefundForm";
import { useBSM } from "./useBSM";

export const tabs = {
  details: "details",
  items: "items",
};

export default function ({ iboNumber, events }) {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(15);

  const { hasPermission } = Auth.useContainer();
  const { records, loading, isWaiting, refresh, cancelOrder } = useBSM({
    iboNumber,
    page,
    limit,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showRefundForm, setShowRefundForm] = useState(false);

  const selected = records && records[selectedIndex];

  useEffect(() => {
    if (loading) {
      events.emit("refresh:start");
    } else {
      events.emit("refresh:end");
    }
  }, [loading]);

  // Listen for refresh events on mount and stop listening when unmounted.
  useEffect(() => {
    const listener = () => {
      refresh();
    };

    events.on("refresh", listener);

    return () => {
      events.removeListener("refresh", listener);
    };
  }, [refresh]);

  const canUpdate = hasPermission("membership.transaction.update");
  const canRefund = hasPermission("bsm.refund.write");

  return (
    <div className={styles.container}>
      {records == null ? (
        <Loader />
      ) : (
        <>
          <LoadingContainer isLoading={loading}>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Last</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>ACH ID</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => {
                  return (
                    <tr
                      key={i}
                      onClick={() => {
                        setSelectedIndex(i);
                      }}
                      className={classNames(styles.resultRow, {
                        [styles.selected]: selectedIndex === i,
                      })}
                    >
                      <td>{r.id}</td>
                      <td>{formatDate(r.dateOrdered)}</td>
                      <td>{r.lastName}</td>
                      <td>{(r.total && r.total.toFixed(2)) || <Empty />}</td>
                      <td>{r.status || <Empty />}</td>
                      <td>{r.ahTraceId || <Empty />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <div className={styles.pagingControls}>
              <PagingControls
                page={page}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
                disableNext={records.length < limit}
                disabled={loading || isWaiting}
              />
            </div>
          </LoadingContainer>

          <div className={styles.actions}>
            {canUpdate && (
              <Button
                purpose={ButtonPurpose.delete}
                disabled={loading || isWaiting}
                onClick={() => {
                  if (selected) {
                    cancelOrder(selected.id);
                  }
                }}
              >
                Cancel Order
              </Button>
            )}

            {canRefund && (
              <Button
                purpose={ButtonPurpose.generic}
                disabled={showRefundForm || loading || isWaiting}
                onClick={() => {
                  setShowRefundForm(true);
                }}
              >
                Refund Order
              </Button>
            )}
          </div>

          {selected && showRefundForm && (
            <RefundForm
              iboNumber={iboNumber}
              order={selected}
              onCancel={() => {
                setShowRefundForm(false);
              }}
            />
          )}

          {selected && (
            <>
              <Row className={styles.borderedRow}>
                <Column className={styles.borderedCol}>
                  <header className={styles.transactionsHeader}>
                    <h3 className={styles.headerText}>Transactions</h3>
                  </header>

                  <Table>
                    <thead>
                      <th>Date</th>
                      <th>Invoice</th>
                      <th>Amount</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>CC Last Four</th>
                      <th>CC Exp</th>
                    </thead>
                    <tbody>
                      {selected.transactions.map((t) => {
                        return (
                          <tr title={JSON.stringify(t, null, 2)}>
                            <td>{formatDate(t.transactionDate)}</td>
                            <td>{t.invoice}</td>
                            <td>{t.amount}</td>
                            <td>{t.type}</td>
                            <td>{t.responseStatus}</td>
                            <td>{t.ccLastFour}</td>
                            <td>{t.ccExp}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Column>
              </Row>
              <Row>
                <Column width="50%" className={styles.paddedColumn}>
                  <Details record={selected} />
                </Column>
                <Column width="50%">
                  <Items record={selected} />
                </Column>
              </Row>
            </>
          )}
        </>
      )}
    </div>
  );
}

export function Empty() {
  return <span className={styles.emptyPlaceholder}>---</span>;
}
